import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  PasswordInput,
  Button,
  Title,
  Text,
  Image,
  Stack,
  Alert,
} from "@mantine/core";
import { IconAlertCircle, IconInfoCircle } from "@tabler/icons-react";
import { authService } from "../services";
import { useAuthStore } from "../store";
import { validatePassword } from "../utils/passwordValidation";
import PasswordStrengthIndicator from "../components/common/PasswordStrengthIndicator";

const GCP_LOGO_URL =
  "https://i0.wp.com/gulfcoastpath.com/wp-content/uploads/2021/02/cropped-GCP-Logo-v1.png?fit=1628%2C1071&ssl=1";

export default function ForceChangePassword() {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { profile } = useAuthStore();

  const { valid: passwordValid } = validatePassword(newPassword);
  const passwordsMatch =
    newPassword.length > 0 && newPassword === confirmPassword;
  const canSubmit = passwordValid && passwordsMatch && oldPassword.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!passwordValid) {
      setError("New password does not meet complexity requirements");
      return;
    }

    if (!passwordsMatch) {
      setError("Passwords do not match");
      return;
    }

    if (oldPassword === newPassword) {
      setError("New password must be different from your current password");
      return;
    }

    setLoading(true);

    try {
      await authService.changePassword({ oldPassword, newPassword });
      const pathologistGuardEnabled =
        process.env.REACT_APP_ENABLE_PATHOLOGIST_GUARD !== "false";
      const isPathologist = profile?.roleName === "Pathologist";
      navigate(
        isPathologist && pathologistGuardEnabled
          ? "/pathologist-review"
          : "/dashboard"
      );
    } catch (err: unknown) {
      const errorMessage =
        err && typeof err === "object" && "message" in err
          ? (err as { message: string }).message
          : "Failed to change password";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container size={420} my={40}>
      <Stack align="center" mb="xl">
        <Image src={GCP_LOGO_URL} alt="GCP" h={80} fit="contain" />
        <Title order={2} c="gcpBlue">
          Change Your Password
        </Title>
        <Text c="dimmed" size="sm">
          You must change your password before continuing
        </Text>
      </Stack>

      <Paper withBorder shadow="md" p={30} radius="md">
        <form
          onSubmit={(e) => {
            handleSubmit(e).catch(() => {});
          }}
        >
          <Stack>
            <Alert
              icon={<IconInfoCircle size={16} />}
              color="blue"
              variant="light"
            >
              Your administrator requires you to set a new password.
            </Alert>

            {error && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="red"
                variant="light"
              >
                {error}
              </Alert>
            )}

            <PasswordInput
              label="Current Password"
              placeholder="Enter your current password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              required
            />

            <PasswordInput
              label="New Password"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />

            <PasswordStrengthIndicator password={newPassword} />

            <PasswordInput
              label="Confirm New Password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              error={
                confirmPassword && !passwordsMatch
                  ? "Passwords do not match"
                  : undefined
              }
            />

            <Button
              type="submit"
              fullWidth
              loading={loading}
              disabled={!canSubmit}
            >
              Set New Password
            </Button>
          </Stack>
        </form>
      </Paper>
    </Container>
  );
}
